import {useEffect, useLayoutEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import {Box, ClickAwayListener, Grid, Typography} from '@mui/material';

import {UserMentionList} from '../UserMentionList/UserMentionList';

import {useResizeObserver} from '../../hooks/useResizeObserver';
import {greenColors, orangeColors} from '../../layouts/theme/designSystem';
import {Option} from '../../types/shared-types';
import {UserMention} from '../../types/UserMention';
import {atSymbolTrigger} from '../../utils/regex/atSymbolTrigger/atSymbolTrigger';

interface Coords {
  x: number;
  y: number;
}

interface Props {
  onChange?: (value: string) => void;
  availableUserMentions: UserMention[];
  readonly?: boolean;
  initialValue?: string;
  shouldReset?: boolean;
  commentId?: string;
}

export const MentionComment = ({
  onChange,
  availableUserMentions,
  readonly,
  initialValue,
  shouldReset,
  commentId,
}: Props) => {
  // Track focus state for placeholder
  const [isFocused, setIsFocused] = useState(false);
  const commentBoxRef = useRef<HTMLElement>(null);
  const mentionSelectRef = useRef<HTMLElement>(null);
  const atContainerRef = useRef<HTMLElement>(null);

  const [sanitizedValue, setSanitizedValue] = useState<string>('');
  const [isSelectingMention, setIsSelectingMention] = useState(false);
  const [shouldSaveMention, setShouldSaveMention] = useState(false);
  const [parsedText, setParsedText] = useState<string[]>([]);
  const [mentionSearch, setMentionSearch] = useState('');
  // This state change allows the userMentionList to be properly positioned. It will be removed in a refactor.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_caretCoords, setCaretCoords] = useState<Coords | undefined>();
  const [keyboardCode, setKeyboardCode] = useState<string>();
  const [isSymbolKey, setIsSymbolKey] = useState(false);
  const [isScrolledIntoView, setIsScrolledIntoView] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedCommentId = searchParams.get('comment');
  const isCommentFromUrl = selectedCommentId === commentId;
  const {t} = useTranslation('translation', {
    keyPrefix: 'companyApplicants.applicantsCard.cardDetails.companyMatchNotes',
  });

  const focusElement = (element: HTMLElement) => {
    // The devilry below is to set the caret at the end of the string within the focused element. Solution from here:
    // https://artificialworlds.net/blog/2022/09/13/tips-for-contenteditables/
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse();
    const sel = document.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    element.focus();
  };

  const focusMentionSelect = () => {
    if (atContainerRef.current && mentionSelectRef.current) {
      focusElement(mentionSelectRef.current);
    }
  };

  useEffect(() => {
    if (isCommentFromUrl) {
      const commentElement = document.getElementById(`section-${commentId}`);
      if (commentElement) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setIsScrolledIntoView(true);
                observer.disconnect();
              }
            });
          },
          {threshold: 0.1}
        );
        observer.observe(commentElement);
      }
    }
  }, [commentId, isCommentFromUrl]);

  useEffect(() => {
    if (isScrolledIntoView) {
      const commentElement = document.getElementById(`section-${commentId}`);
      if (commentElement) {
        commentElement.classList.add('flash-light');
        setTimeout(() => {
          commentElement.classList.remove('flash-light');
        }, 2000);
      }
    }
  }, [isScrolledIntoView, commentId]);

  const {refCallback, dimensions} = useResizeObserver();

  const onMentionSearch = (e: any) => {
    if (e.code === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const text = e.target.innerHTML === '<br>' ? '' : e.target.innerHTML;
    setMentionSearch(text);

    if (e.code === 'Space') {
      saveMentionText(`@${text}`);
    }
  };

  const handleListControlKeys = (e: any) => {
    const specialCharacters = ['Enter', 'ArrowUp', 'ArrowDown'];
    if (e.code === 'Space') {
      saveMentionText(`@${e.target.innerText}`);
      return;
    }

    if (specialCharacters.includes(e.code)) {
      setKeyboardCode(e.code);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  const handleSpecialKeys = (e: any) => {
    if (e.code === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.code === 'Backspace' && mentionSearch === '') {
      e.preventDefault();
      saveMentionText('');
    }

    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      saveMentionText('');
    }
  };

  const handleOutsideClick = () => {
    if (mentionSearch) {
      saveMentionText(`@${mentionSearch}`);
    } else {
      saveMentionText('@');
    }
  };

  const onTextChange = (e: any) => {
    if (e.target.innerHTML === '<br>') {
      setSanitizedValue('');
      onChange && onChange('');
    } else {
      setSanitizedValue(e.target.innerHTML);
      onChange && onChange(e.target.innerHTML);
    }
  };

  const saveMentionText = (update: string) => {
    let joinedText;

    if (parsedText.every((entry) => !!entry)) {
      joinedText = `${parsedText[0]} ${update} ${parsedText[1]}`;
    } else {
      joinedText = parsedText
        .map((entry) => {
          if (entry) {
            return entry;
          } else {
            return update;
          }
        })
        .join(' ')
        .trim();
    }
    setSanitizedValue(joinedText);
    setShouldSaveMention(true);
    setIsSelectingMention(false);
    setParsedText([]);
    setMentionSearch('');
    onChange && onChange(joinedText);
  };

  const onMentionSelect = (option: Option) => {
    const text = `<mention contenteditable="false" resource_id=${option.id}>@${option.label}</mention>`;
    saveMentionText(text);
  };

  useLayoutEffect(() => {
    if (!isSelectingMention) return;

    const mentionSelect = mentionSelectRef.current;
    const atSymbol = atContainerRef.current;
    const elements = parsedText.map((_text, idx) => {
      return document.getElementById(`frozen-text-${idx}`);
    });

    elements.forEach((element, idx) => {
      if (element) {
        element.innerHTML = parsedText[idx];
      }
    });

    if (mentionSelect && atSymbol) {
      focusElement(mentionSelect);
    }
  }, [isSelectingMention, sanitizedValue, parsedText]);

  useLayoutEffect(() => {
    const body = document.getElementsByTagName('body');
    if (!body.length) return;

    if (isSelectingMention) {
      body[0].classList.add('disable-scroll');
    } else {
      body[0].classList.remove('disable-scroll');
    }
  }, [isSelectingMention]);

  useLayoutEffect(() => {
    if (shouldSaveMention) {
      setShouldSaveMention(false);

      const element = commentBoxRef.current;

      if (element) {
        element.innerHTML = sanitizedValue;

        focusElement(element);
      }
    }
  }, [shouldSaveMention, sanitizedValue]);

  useEffect(() => {
    if (initialValue) {
      const element = commentBoxRef.current;

      if (element) {
        element.innerHTML = initialValue;
        setSanitizedValue(initialValue);
      }
    }
  }, [initialValue]);

  useLayoutEffect(() => {
    const mentionSelect = mentionSelectRef.current;
    const atSymbol = atContainerRef.current;

    if (isSelectingMention && mentionSelect && atSymbol) {
      setCaretCoords({
        x: atSymbol.offsetLeft + atSymbol.offsetWidth,
        y: atSymbol.getBoundingClientRect().top - atSymbol.offsetHeight - 12,
      });
    }

    if (!isSelectingMention) {
      setCaretCoords(undefined);
    }
  }, [dimensions, isSelectingMention]);

  useEffect(() => {
    const commentBox = commentBoxRef.current;
    if (!readonly && commentBox) {
      focusElement(commentBox);
    }
  }, [readonly]);

  useEffect(() => {
    const commentBox = commentBoxRef.current;

    if (shouldReset && commentBox) {
      setSanitizedValue('');
      commentBox.innerHTML = '';
    }
  }, [shouldReset]);

  useEffect(() => {
    if (keyboardCode) {
      setKeyboardCode(undefined);
    }
  }, [keyboardCode]);

  const onSymbolType = (e: any) => {
    if (e.key === '@') {
      setIsSymbolKey(true);
      return;
    }

    if (isSymbolKey) {
      setIsSymbolKey(false);
    }
  };

  useEffect(() => {
    if (atSymbolTrigger.test(sanitizedValue) && isSymbolKey) {
      setIsSymbolKey(false);
      // Remove @ symbol. It will be added to the new form that opens.
      const textTest = sanitizedValue.split(atSymbolTrigger);
      if (textTest.every((entry) => !entry)) {
        setParsedText(['']);
      } else if (textTest.every((entry) => !!entry)) {
        setParsedText([textTest[0], '', textTest[1]]);
      } else {
        setParsedText(textTest);
      }

      setIsSelectingMention(true);
    }
  }, [sanitizedValue, isSymbolKey]);

  return (
    <Grid ref={refCallback}>
      <Grid
        data-testid={`section-${commentId}`}
        id={`section-${commentId}`}
        minHeight='50px'
        width='100%'
        padding='8px'
        sx={{
          pointerEvents: readonly ? 'none' : 'initial',
          backgroundColor: !readonly ? (isFocused ? orangeColors[7] : orangeColors[5]) : orangeColors[5],
          transition: 'background-color 0.2s',
          color: readonly ? greenColors[600] : 'initial',
        }}
        className={'mention-comment-light'}
        borderRadius={'5px'}
      >
        {!isSelectingMention ? (
          <Box position='relative' width='100%' minHeight='50px'>
            {(!sanitizedValue || sanitizedValue === '<br>') && !readonly && !isFocused && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: 0,
                  display: 'flex',
                  alignItems: 'center',
                  color: greenColors[100],
                }}
              >
                <CommentOutlinedIcon sx={{mr: 1, color: greenColors[100], fontSize: 20}} />
                <Typography
                  sx={{
                    color: greenColors[100],
                    pointerEvents: 'none',
                  }}
                  variant='body1'
                >
                  {t('placeholder')}
                </Typography>
              </Box>
            )}
            <Typography
              ref={commentBoxRef}
              data-testid='mention-comment-box'
              suppressContentEditableWarning
              display='block'
              width='100%'
              minWidth='100%'
              minHeight='50px'
              component='span'
              onInput={onTextChange as any}
              onKeyDown={onSymbolType}
              contentEditable={!readonly && !isSelectingMention}
              sx={{position: 'relative', zIndex: 1}}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            ></Typography>
          </Box>
        ) : (
          <ClickAwayListener onClickAway={handleOutsideClick}>
            <Box component='span'>
              <Box width='100%' minHeight='50px' component='p' margin={0} onClick={focusMentionSelect}>
                {parsedText.map((entry, idx) => {
                  if (entry) {
                    return (
                      <Typography
                        key={entry}
                        onClick={handleOutsideClick}
                        id={`frozen-text-${idx}`}
                        component='span'
                        contentEditable={false}
                      >
                        {entry}
                      </Typography>
                    );
                  }
                  return (
                    <Typography
                      key={`@-${idx}`}
                      component='span'
                      sx={{
                        cursor: 'pointer',
                        display: 'inline-block',
                        color: greenColors[500],
                      }}
                      ref={atContainerRef}
                      onClick={focusMentionSelect}
                    >
                      &nbsp;@
                      <Typography
                        data-testid='mention-search'
                        component='span'
                        ref={mentionSelectRef}
                        suppressContentEditableWarning
                        onInput={onMentionSearch}
                        onKeyUp={handleListControlKeys}
                        onKeyDown={handleSpecialKeys}
                        contentEditable={true}
                        sx={{
                          display: 'inline-block',
                          color: greenColors[700],
                        }}
                      ></Typography>
                    </Typography>
                  );
                })}
              </Box>
              <UserMentionList
                keyboardCode={keyboardCode}
                anchorElement={mentionSelectRef.current}
                shouldOpen={isSelectingMention}
                options={availableUserMentions.map((mention) => ({
                  id: mention.resourceId,
                  label: `${mention.firstName} ${mention.lastName}`,
                }))}
                textSearch={mentionSearch}
                onClick={onMentionSelect}
              />
            </Box>
          </ClickAwayListener>
        )}
      </Grid>
    </Grid>
  );
};
